import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LogOut, Mail, Inbox, CheckCircle2, Loader2, ExternalLink, Upload, Trash2, FileText } from "lucide-react";

const STATUS_OPTIONS = ["new", "contacted", "closed"];

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0 });
  const [certs, setCerts] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [uploadingCode, setUploadingCode] = useState(null);
  const fileInputs = useRef({});

  useEffect(() => {
    if (!loading && (user === false || !user)) navigate("/admin/login");
  }, [user, loading, navigate]);

  const load = async () => {
    try {
      const [inq, st, ct] = await Promise.all([
        api.get("/admin/inquiries"),
        api.get("/admin/stats"),
        api.get("/certifications"),
      ]);
      setInquiries(inq.data);
      setStats(st.data);
      setCerts(ct.data);
    } catch {
      /* handled by auth redirect */
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (user && user !== false) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      setInquiries((list) => list.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Failed to update");
    }
  };

  const onFilePicked = (code) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Please upload a file under 5 MB.");
      return;
    }
    setUploadingCode(code);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.put(`/admin/certifications/${code}`, {
          file_data: reader.result,
          file_name: file.name,
          file_type: file.type,
        });
        setCerts((list) => list.map((c) => (c.code === code ? data : c)));
        toast.success(`${code} certificate uploaded`);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
      } finally {
        setUploadingCode(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const removeFile = async (code) => {
    try {
      await api.delete(`/admin/certifications/${code}`);
      setCerts((list) => list.map((c) => (c.code === code ? { ...c, file_data: null, file_name: null, file_type: null } : c)));
      toast.success(`${code} certificate removed`);
    } catch {
      toast.error("Failed to remove");
    }
  };

  if (loading || !user || user === false) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-brand-green" />
      </div>
    );
  }

  const tabBtn = (id, label) =>
    `px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
      tab === id ? "bg-brand-green text-white" : "bg-white border border-brand-line text-brand-charcoal hover:border-brand-gold"
    }`;

  return (
    <div className="min-h-screen bg-brand-cream">
      <div className="bg-brand-green-dark text-brand-cream">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-xl font-semibold">SDRC <span className="text-brand-gold">Global</span> <span className="text-brand-cream/50 text-sm font-sans">/ Admin</span></Link>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm border border-brand-cream/25 px-4 py-2 rounded-full hover:bg-brand-cream/10 transition-colors">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        <div className="flex items-center gap-3 mb-8">
          <button className={tabBtn("inquiries", "Inquiries")} onClick={() => setTab("inquiries")} data-testid="tab-inquiries">Inquiries</button>
          <button className={tabBtn("certifications", "Certifications")} onClick={() => setTab("certifications")} data-testid="tab-certifications">Certifications</button>
        </div>

        {tab === "inquiries" && (
          <>
            <h1 className="font-display text-4xl font-medium text-brand-green">Inquiries</h1>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
              <div className="bg-white border border-brand-line p-6" data-testid="stat-total">
                <div className="flex items-center gap-2 text-brand-muted text-sm"><Inbox size={16} /> Total</div>
                <div className="font-display text-4xl text-brand-green mt-2">{stats.total}</div>
              </div>
              <div className="bg-white border border-brand-line p-6" data-testid="stat-new">
                <div className="flex items-center gap-2 text-brand-muted text-sm"><Mail size={16} /> New</div>
                <div className="font-display text-4xl text-brand-gold-dark mt-2">{stats.new}</div>
              </div>
              <div className="bg-white border border-brand-line p-6 hidden md:block">
                <div className="flex items-center gap-2 text-brand-muted text-sm"><CheckCircle2 size={16} /> Handled</div>
                <div className="font-display text-4xl text-brand-green mt-2">{Math.max(stats.total - stats.new, 0)}</div>
              </div>
            </div>

            <div className="bg-white border border-brand-line mt-8 overflow-x-auto" data-testid="inquiries-table">
              {fetching ? (
                <div className="p-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-brand-green" /></div>
              ) : inquiries.length === 0 ? (
                <div className="p-12 text-center text-brand-muted">No inquiries yet.</div>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-brand-line text-brand-muted uppercase text-xs tracking-wider">
                      <th className="p-4">Contact</th>
                      <th className="p-4 hidden md:table-cell">Company / Country</th>
                      <th className="p-4 hidden lg:table-cell">Product / Qty</th>
                      <th className="p-4">Message</th>
                      <th className="p-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inquiries.map((i) => (
                      <tr key={i.id} className="border-b border-brand-line align-top hover:bg-brand-cream/50" data-testid={`inquiry-row-${i.id}`}>
                        <td className="p-4">
                          <div className="font-medium text-brand-charcoal">{i.name}</div>
                          <a href={`mailto:${i.email}`} className="text-brand-green hover:underline inline-flex items-center gap-1 text-xs mt-1">{i.email} <ExternalLink size={11} /></a>
                          {i.phone && <div className="text-xs text-brand-muted mt-1">{i.phone}</div>}
                          <div className="text-[11px] text-brand-muted/70 mt-1">{new Date(i.created_at).toLocaleString()}</div>
                          {i.email_status && i.email_status !== "sent" && (
                            <span className={`inline-block text-[10px] uppercase tracking-wider mt-1 px-2 py-0.5 rounded-full ${i.email_status === "skipped" ? "bg-brand-gold/15 text-brand-gold-dark" : "bg-red-100 text-red-600"}`} title={i.email_error || ""}>
                              email: {i.email_status}
                            </span>
                          )}
                        </td>
                        <td className="p-4 hidden md:table-cell text-brand-charcoal">
                          <div>{i.company_name || "—"}</div>
                          <div className="text-xs text-brand-muted mt-1">{i.country || "—"}</div>
                        </td>
                        <td className="p-4 hidden lg:table-cell text-brand-charcoal">
                          <div>{i.product_interest || "—"}</div>
                          <div className="text-xs text-brand-muted mt-1">{i.quantity || "—"}</div>
                        </td>
                        <td className="p-4 text-brand-muted max-w-xs">{i.message || "—"}</td>
                        <td className="p-4">
                          <Select value={i.status} onValueChange={(v) => updateStatus(i.id, v)}>
                            <SelectTrigger data-testid={`status-select-${i.id}`} className="w-32 h-9 text-xs capitalize"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {tab === "certifications" && (
          <>
            <h1 className="font-display text-4xl font-medium text-brand-green">Certifications</h1>
            <p className="text-brand-muted mt-2 text-sm">Upload certificate files (image or PDF, under 5 MB). They appear on the public Food Safety page.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="cert-manager">
              {certs.map((c) => (
                <div key={c.code} className="bg-white border border-brand-line p-6" data-testid={`cert-card-${c.code}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-2xl text-brand-green">{c.code}</h3>
                    <span className={`text-[11px] uppercase tracking-[0.15em] font-semibold px-3 py-1 rounded-full ${c.status === "Registered" ? "bg-brand-green/10 text-brand-green" : "bg-brand-gold/15 text-brand-gold-dark"}`}>{c.status}</span>
                  </div>
                  <p className="text-sm text-brand-charcoal mt-1">{c.name}</p>

                  <div className="mt-4 border border-dashed border-brand-line rounded-sm h-40 flex items-center justify-center overflow-hidden bg-brand-cream">
                    {c.file_data ? (
                      (c.file_type || "").includes("pdf") || (c.file_name || "").toLowerCase().endsWith(".pdf") ? (
                        <div className="flex flex-col items-center text-brand-green"><FileText size={34} /><span className="text-xs mt-2 text-brand-muted">{c.file_name}</span></div>
                      ) : (
                        <img src={c.file_data} alt={c.name} className="w-full h-full object-contain" />
                      )
                    ) : (
                      <span className="text-xs text-brand-muted">No file uploaded</span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mt-4">
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      className="hidden"
                      ref={(el) => (fileInputs.current[c.code] = el)}
                      onChange={onFilePicked(c.code)}
                      data-testid={`cert-file-input-${c.code}`}
                    />
                    <button
                      onClick={() => fileInputs.current[c.code]?.click()}
                      disabled={uploadingCode === c.code}
                      data-testid={`cert-upload-${c.code}`}
                      className="inline-flex items-center gap-2 bg-brand-green text-white text-sm px-4 py-2 rounded-full hover:bg-brand-green-dark transition-colors disabled:opacity-60"
                    >
                      {uploadingCode === c.code ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      {c.file_data ? "Replace" : "Upload"}
                    </button>
                    {c.file_data && (
                      <button
                        onClick={() => removeFile(c.code)}
                        data-testid={`cert-remove-${c.code}`}
                        className="inline-flex items-center gap-2 border border-brand-line text-brand-charcoal text-sm px-4 py-2 rounded-full hover:border-red-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 size={14} /> Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
